import React, { useCallback, useState } from 'react';
import { BarChart2, Save, RefreshCw, Download, Calculator, AlertCircle } from 'lucide-react';
import Button from '../../../../../components/ui/Button';

// Импорт секций формы
import MortgageParametersSection from './MortgageParametersSection';
import CurrencyOptionsSection from './CurrencyOptionsSection';
import AdditionalParametersSection from './AdditionalParametersSection';
import EarlyRepaymentSection from './EarlyRepaymentSection';
import FeesSection from './FeesSection';
import RiskSection from './RiskSection';

// Импорт утилитарных функций
import { isPositiveNumber, isInteger } from '../../../../../utils/validation';
import { formatCurrency } from '../../../../../utils/formatting';
import { convertCurrency } from '../../../../../utils/currency';

/**
 * Компонент основной формы расчета валютной ипотеки
 */
const CalculationForm = ({ 
  formData, 
  setFormData, 
  errors, 
  setErrors, 
  isCalculating, 
  setIsCalculating,
  results,
  setResults,
  schedule,
  setSchedule,
  setRiskAnalysis,
  supportedCurrencies,
  earlyRepaymentPeriods,
  exchangeRateScenarios,
  riskToleranceLevels,
  currencyRates,
  historicalRates,
  saveLoan 
}) => {
  
  // Состояние для имени сохраняемого кредита
  const [saveLoanName, setSaveLoanName] = useState('');
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  
  // Получение символа валюты
  const getCurrencySymbol = (currencyCode) => {
    const currency = supportedCurrencies.find(c => c.id === currencyCode);
    return currency ? currency.symbol : currencyCode;
  };
  
  // Функция парсинга числа с плавающей точкой или возврат значения по умолчанию
  const parseFloatOrDefault = (val, defaultVal = NaN) => {
    const parsed = parseFloat(String(val).replace(',', '.'));
    return isNaN(parsed) ? defaultVal : parsed;
  };
  
  // Функция парсинга целого числа или возврат значения по умолчанию
  const parseIntOrDefault = (val, defaultVal = NaN) => {
    const parsed = parseInt(String(val), 10);
    return isNaN(parsed) ? defaultVal : parsed;
  };
  
  // Обработчик изменения полей ввода
  const handleInputChange = useCallback((e) => {
    const { name, value, type, checked } = e.target;
    const fieldValue = type === 'checkbox' ? checked : value;

    setFormData(prev => {
      const newState = { ...prev, [name]: fieldValue };

      // Обновляем процентную ставку автоматически при изменении валюты
      if (name === 'currency') {
        const selectedCurrency = supportedCurrencies.find(c => c.id === fieldValue);
        if (selectedCurrency) {
          const currentRate = parseFloatOrDefault(prev.interestRate, 0);
          const isRateDefault = supportedCurrencies.some(c => currentRate === c.defaultRate);
          if (isRateDefault || !prev.interestRate || currentRate <= 0) {
            newState.interestRate = selectedCurrency.defaultRate.toString();
          }
          
          // Обновляем эквивалент в рублях
          const loanAmount = parseFloatOrDefault(prev.loanAmount, 0);
          if (loanAmount > 0 && currencyRates[fieldValue]) {
            newState.equivalentInRubles = Math.round(loanAmount * currencyRates[fieldValue]).toString();
          }
        }
      }

      // Если изменена сумма кредита, обновляем эквивалент в рублях
      if (name === 'loanAmount' && currencyRates[prev.currency]) {
        const loanAmount = parseFloatOrDefault(value, 0);
        if (loanAmount > 0) {
          newState.equivalentInRubles = Math.round(loanAmount * currencyRates[prev.currency]).toString();
        }
      }

      // Сбрасываем связанные поля если функция отключена
      if (name === 'includeEarlyRepayment' && !checked) {
        newState.earlyRepaymentType = 'reduction';
        newState.earlyRepaymentAmount = '1000';
        newState.earlyRepaymentPeriod = 'monthly';
        newState.earlyRepaymentStartMonth = '6';
      }
      
      if (name === 'includeFees' && !checked) {
        newState.initialFee = '0';
        newState.monthlyFee = '0';
        newState.monthlyFeeType = 'fixed';
      }

      if (name === 'hasIncomeInForeignCurrency' && !checked) {
        newState.monthlyIncomeForeign = '0';
      }

      return newState;
    });

    // Очищаем ошибку для изменяемого поля
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  }, [supportedCurrencies, currencyRates, errors, setErrors, setFormData]);

  // Валидация формы
  const validateForm = useCallback(() => {
    const newErrors = {};
    const {
      loanAmount, loanTerm, interestRate, startDate, currency,
      includeEarlyRepayment, earlyRepaymentAmount, earlyRepaymentStartMonth, earlyRepaymentPeriod, earlyRepaymentType,
      includeFees, initialFee, monthlyFee, monthlyFeeType, paymentType,
      hasIncomeInForeignCurrency, monthlyIncomeRUB, monthlyIncomeForeign,
      initialDownPayment, currencyRiskTolerance, exchangeRateScenario
    } = formData;

    // Парсинг числовых значений
    const numLoanAmount = parseFloatOrDefault(loanAmount, -1);
    const numLoanTermYears = parseFloatOrDefault(loanTerm, -1);
    const numLoanTermMonths = Math.round(numLoanTermYears * 12);
    const numInterestRate = parseFloatOrDefault(interestRate, -1);
    const numInitialDownPayment = parseFloatOrDefault(initialDownPayment, -1);

    // Валидация базовых полей
    if (numLoanAmount <= 0) newErrors.loanAmount = 'Сумма должна быть положительной.';
    if (!newErrors.loanAmount && numLoanAmount > 10000000) newErrors.loanAmount = `Максимальная сумма 10 млн ${currency}.`;
    
    if (numLoanTermYears <= 0) newErrors.loanTerm = 'Срок должен быть положительным.';
    if (!newErrors.loanTerm && numLoanTermYears > 30) newErrors.loanTerm = 'Максимальный срок 30 лет.';
    if (!newErrors.loanTerm && numLoanTermMonths <= 0) newErrors.loanTerm = 'Срок должен быть не менее 1 месяца.';
    
    if (numInterestRate <= 0) newErrors.interestRate = 'Ставка должна быть положительной.';
    if (!newErrors.interestRate && numInterestRate > 20) newErrors.interestRate = 'Ставка не может быть более 20%.';
    
    if (numInitialDownPayment < 0) newErrors.initialDownPayment = 'Первоначальный взнос не может быть отрицательным.';
    
    // Проверка валюты
    if (!currency || !supportedCurrencies.some(c => c.id === currency)) {
      newErrors.currency = 'Выберите валюту кредита.';
    }

    // Проверка даты начала кредита
    if (!startDate) newErrors.startDate = 'Укажите дату начала кредита.';
    try {
      const dateObject = new Date(startDate);
      if (isNaN(dateObject.getTime())) {
        newErrors.startDate = 'Некорректная дата.';
      }
    } catch (e) {
      newErrors.startDate = 'Некорректная дата.';
    }

    // Валидация дохода
    const numIncomeRUB = parseFloatOrDefault(monthlyIncomeRUB, 0);
    if (numIncomeRUB <= 0) {
      newErrors.monthlyIncomeRUB = 'Укажите доход в рублях.';
    }
    
    if (hasIncomeInForeignCurrency) {
      const numIncomeForeign = parseFloatOrDefault(monthlyIncomeForeign, 0);
      if (numIncomeForeign <= 0) {
        newErrors.monthlyIncomeForeign = 'Укажите доход в валюте.';
      }
    }

    // Валидация досрочного погашения
    if (includeEarlyRepayment) {
      const numEarlyRepaymentAmount = parseFloatOrDefault(earlyRepaymentAmount, -1);
      const numEarlyRepaymentStartMonth = parseIntOrDefault(earlyRepaymentStartMonth, -1);
      if (numEarlyRepaymentAmount <= 0) newErrors.earlyRepaymentAmount = 'Сумма должна быть положительной.';
      if (!newErrors.earlyRepaymentAmount && numEarlyRepaymentAmount >= numLoanAmount) {
        newErrors.earlyRepaymentAmount = 'Сумма досрочного погашения не может быть больше или равна сумме кредита.';
      }
      if (!isInteger(earlyRepaymentStartMonth) || numEarlyRepaymentStartMonth <= 0) {
        newErrors.earlyRepaymentStartMonth = 'Введите целый полож. номер месяца.';
      }
      if (!newErrors.loanTerm && !newErrors.earlyRepaymentStartMonth && numEarlyRepaymentStartMonth > numLoanTermMonths) {
        newErrors.earlyRepaymentStartMonth = `Начало (${numEarlyRepaymentStartMonth}) не может быть позже срока (${numLoanTermMonths} мес.).`;
      }
      if (!earlyRepaymentPeriod) newErrors.earlyRepaymentPeriod = "Выберите периодичность.";
      if (!earlyRepaymentType) newErrors.earlyRepaymentType = "Выберите цель погашения.";
    }

    // Валидация комиссий
    if (includeFees) {
      const numInitialFee = parseFloatOrDefault(initialFee, -1);
      const numMonthlyFee = parseFloatOrDefault(monthlyFee, -1);
      
      if (numInitialFee < 0) newErrors.initialFee = 'Значение не может быть отрицательным.';
      if (numMonthlyFee < 0) newErrors.monthlyFee = 'Значение не может быть отрицательным.';
      
      if (monthlyFeeType === 'percent' && numMonthlyFee > 100) {
        newErrors.monthlyFee = 'Процент не может быть больше 100%.';
      }
    }

    // Валидация типа платежа
    if (!paymentType || !['annuity', 'differentiated'].includes(paymentType)) {
      newErrors.paymentType = "Выберите корректный тип платежа.";
    }
    
    // Валидация параметров риска
    if (!currencyRiskTolerance || !riskToleranceLevels.some(t => t.id === currencyRiskTolerance)) {
      newErrors.currencyRiskTolerance = "Выберите уровень толерантности к риску.";
    }
    
    if (!exchangeRateScenario || !exchangeRateScenarios.some(s => s.id === exchangeRateScenario)) {
      newErrors.exchangeRateScenario = "Выберите сценарий изменения курса.";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData, setErrors, supportedCurrencies, exchangeRateScenarios, riskToleranceLevels]);

  // Расчет аннуитетного платежа
  const calculateAnnuityPayment = (principal, termMonths, annualRate) => {
    if (principal <= 0 || termMonths <= 0) return 0;
    const monthlyRate = annualRate / 12 / 100;

    if (monthlyRate === 0) {
      return principal / termMonths;
    }

    const factor = Math.pow(1 + monthlyRate, termMonths);
    if (!isFinite(factor) || factor === 1) {
      return principal / termMonths;
    }
    return principal * monthlyRate * factor / (factor - 1);
  };

  // Функция расчета графика платежей с учетом валютных рисков
  const calculatePaymentSchedule = useCallback(() => {
    if (!validateForm()) {
      // Опционально прокрутка к первой ошибке
      const firstErrorKey = Object.keys(errors)[0];
      if (firstErrorKey) {
        const errorElement = document.getElementById(firstErrorKey);
        errorElement?.focus();
        errorElement?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }

    setIsCalculating(true);
    setResults(null);
    setSchedule([]);
    setRiskAnalysis(null);

    // Используем setTimeout для обновления UI перед расчетами
    setTimeout(() => {
      try {
        // --- Подготавливаем входные данные ---
        const numLoanAmount = parseFloatOrDefault(formData.loanAmount);
        const initialLoanAmount = numLoanAmount;
        const selectedCurrency = formData.currency;
        const currencyRate = currencyRates[selectedCurrency] || 1;

        if (initialLoanAmount <= 0) {
          setErrors({ general: "Сумма кредита должна быть положительной." });
          setIsCalculating(false);
          return;
        }

        const initialLoanTermYears = parseFloatOrDefault(formData.loanTerm);
        const initialLoanTermMonths = Math.round(initialLoanTermYears * 12);
        const initialAnnualRate = parseFloatOrDefault(formData.interestRate);
        const paymentType = formData.paymentType;
        const startDateObj = new Date(formData.startDate);

        const includeEarlyRepayment = formData.includeEarlyRepayment;
        const earlyRepaymentType = formData.earlyRepaymentType;
        const earlyRepaymentAmount = includeEarlyRepayment ? parseFloatOrDefault(formData.earlyRepaymentAmount, 0) : 0;
        const earlyRepaymentPeriod = formData.earlyRepaymentPeriod;
        const earlyRepaymentStartMonth = includeEarlyRepayment ? parseIntOrDefault(formData.earlyRepaymentStartMonth, 1) : 1;

        const includeFees = formData.includeFees;
        const initialFee = includeFees ? parseFloatOrDefault(formData.initialFee, 0) : 0;
        const monthlyFee = includeFees ? parseFloatOrDefault(formData.monthlyFee, 0) : 0;
        const monthlyFeeType = formData.monthlyFeeType;
        
        const initialDownPayment = parseFloatOrDefault(formData.initialDownPayment, 0);
        const hasIncomeInForeignCurrency = formData.hasIncomeInForeignCurrency;
        const monthlyIncomeRUB = parseFloatOrDefault(formData.monthlyIncomeRUB, 0);
        const monthlyIncomeForeign = parseFloatOrDefault(formData.monthlyIncomeForeign, 0);
        const currencyRiskTolerance = formData.currencyRiskTolerance;
        const exchangeRateScenario = formData.exchangeRateScenario;

        // --- Инициализируем переменные для расчета ---
        let currentLoanBalance = initialLoanAmount;
        let remainingTermMonthsForCalc = initialLoanTermMonths;
        let totalPrincipalPaidScheduled = 0;
        let totalInterestPaid = 0;
        let totalInitialFeePaid = initialFee;
        let totalMonthlyFeesPaid = 0;
        let totalEarlyRepaymentPaid = 0;

        const calculatedSchedule = [];
        let currentMonth = 0;

        // Определяем начальный курс валюты
        let currentExchangeRate = currencyRate;
        
        // Определяем параметры сценария изменения курса
        let monthlyRateChange = 0; // Ежемесячное изменение курса
        let volatilityFactor = 0; // Фактор волатильности курса
        
        switch (exchangeRateScenario) {
          case 'optimistic':
            // Укрепление рубля на 10% в год
            monthlyRateChange = -0.008; // -0.8% в месяц
            volatilityFactor = 0.02; // Низкая волатильность
            break;
          case 'moderate':
            // Стабильный курс с инфляцией 4-5% в год
            monthlyRateChange = 0.004; // 0.4% в месяц
            volatilityFactor = 0.03; // Умеренная волатильность
            break;
          case 'pessimistic':
            // Ослабление рубля на 15% в год
            monthlyRateChange = 0.012; // 1.2% в месяц
            volatilityFactor = 0.04; // Повышенная волатильность
            break;
          case 'crisis':
            // Кризисные скачки до 30-50%
            monthlyRateChange = 0.015; // 1.5% в месяц
            volatilityFactor = 0.1; // Высокая волатильность с возможными скачками
            break;
          default:
            monthlyRateChange = 0.004; // По умолчанию как в умеренном сценарии
            volatilityFactor = 0.03;
        }

        // Рассчитываем начальный аннуитетный платеж (если применимо)
        let fixedAnnuityPayment = (paymentType === 'annuity')
            ? calculateAnnuityPayment(initialLoanAmount, initialLoanTermMonths, initialAnnualRate)
            : 0;

        // Определяем частоту досрочного погашения
        let earlyRepaymentFrequencyMonths = 0;
        if (includeEarlyRepayment && earlyRepaymentAmount > 0) {
            switch (earlyRepaymentPeriod) {
                case 'monthly': earlyRepaymentFrequencyMonths = 1; break;
                case 'quarterly': earlyRepaymentFrequencyMonths = 3; break;
                case 'semi_annually': earlyRepaymentFrequencyMonths = 6; break;
                case 'annually': earlyRepaymentFrequencyMonths = 12; break;
                case 'once': earlyRepaymentFrequencyMonths = -1; break;
                default: earlyRepaymentFrequencyMonths = 0;
            }
        }

        // --- Оценка рисков для разных сценариев ---
        const worstCaseRate = currencyRate * (1 + 0.5); // +50% к текущему курсу в худшем случае
        const bestCaseRate = currencyRate * (1 - 0.2); // -20% к текущему курсу в лучшем случае
        
        const baseMonthlyPayment = fixedAnnuityPayment;
        const baseMonthlyPaymentRub = baseMonthlyPayment * currencyRate;
        const worstCaseMonthlyPaymentRub = baseMonthlyPayment * worstCaseRate;
        const bestCaseMonthlyPaymentRub = baseMonthlyPayment * bestCaseRate;
        
        // Оценка нагрузки на бюджет
        const incomeInRUB = monthlyIncomeRUB;
        const incomeInForeignCurrency = hasIncomeInForeignCurrency ? monthlyIncomeForeign * currencyRate : 0;
        const totalMonthlyIncome = incomeInRUB + incomeInForeignCurrency;
        
        const currentDTI = (baseMonthlyPaymentRub / totalMonthlyIncome) * 100; // Debt-to-Income Ratio
        const worstCaseDTI = (worstCaseMonthlyPaymentRub / totalMonthlyIncome) * 100;
        
        // Определение риск-фактора
        let riskFactor = 0;
        switch (currencyRiskTolerance) {
          case 'low': riskFactor = 0.8; break; // Низкая толерантность → жесткая оценка рисков
          case 'medium': riskFactor = 1.0; break; // Средняя толерантность → нормальная оценка
          case 'high': riskFactor = 1.2; break; // Высокая толерантность → более мягкая оценка
          default: riskFactor = 1.0;
        }
        
        // Повышаем риск если нет дохода в валюте кредита
        if (!hasIncomeInForeignCurrency || monthlyIncomeForeign <= 0) {
          riskFactor *= 1.2; // Повышаем риск на 20%
        }
        
        // Рассчитываем риск-индекс
        const riskIndex = (worstCaseDTI / 50) * riskFactor; // 50% DTI как критический уровень
        
        // Определяем уровень риска
        let riskLevel;
        let riskRecommendations = [];
        
        if (riskIndex < 0.7) {
          riskLevel = 'low';
          riskRecommendations = [
            'Ваша валютная ипотека имеет приемлемый уровень риска',
            'Для дополнительной безопасности рассмотрите частичное хеджирование валютных рисков',
            'Поддерживайте резервный фонд на случай кратковременных колебаний курса'
          ];
        } else if (riskIndex < 1.3) {
          riskLevel = 'medium';
          riskRecommendations = [
            'Валютная ипотека несёт умеренный риск для вашего бюджета',
            'Рекомендуется создать резервный фонд в размере 6 месячных платежей',
            'Рассмотрите возможность увеличения дохода в валюте кредита',
            'Отслеживайте динамику курса валют и будьте готовы к рефинансированию'
          ];
        } else {
          riskLevel = 'high';
          riskRecommendations = [
            'Валютная ипотека несёт высокий риск для вашего бюджета',
            'Рекомендуется рассмотреть рублевую ипотеку как более безопасную альтернативу',
            'Если вы решите взять валютную ипотеку, необходим крупный резервный фонд',
            'Критически важно иметь значительную часть доходов в валюте кредита',
            'При первой возможности рассмотрите рефинансирование в рубли'
          ];
        }

        // --- Цикл расчета графика платежей ---
        const maxIterations = initialLoanTermMonths * 2; // Некоторый запас итераций

        while (currentLoanBalance > 0.005 && currentMonth < maxIterations) {
            currentMonth++;
            const paymentDate = new Date(startDateObj);
            paymentDate.setMonth(startDateObj.getMonth() + currentMonth - 1);

            // Рассчитываем ежемесячные проценты
            const monthlyRate = initialAnnualRate / 12 / 100;
            let interestPayment = currentLoanBalance * monthlyRate;
            interestPayment = Math.max(0, interestPayment);

            // Рассчитываем плановое погашение основного долга и ежемесячный платеж
            let principalPaymentScheduled = 0;
            let monthlyPaymentBase = 0;

            if (paymentType === 'annuity') {
                monthlyPaymentBase = fixedAnnuityPayment;
                principalPaymentScheduled = monthlyPaymentBase - interestPayment;

                if (currentLoanBalance + interestPayment <= monthlyPaymentBase + 0.005) {
                    principalPaymentScheduled = currentLoanBalance;
                    monthlyPaymentBase = currentLoanBalance + interestPayment;
                }
            } else { // Дифференцированный
                const basePrincipalPayment = initialLoanAmount / initialLoanTermMonths;
                principalPaymentScheduled = basePrincipalPayment;

                if (currentLoanBalance <= basePrincipalPayment + 0.005) {
                    principalPaymentScheduled = currentLoanBalance;
                }
                monthlyPaymentBase = principalPaymentScheduled + interestPayment;
            }
            principalPaymentScheduled = Math.max(0, principalPaymentScheduled);

            // Имитируем изменение курса валюты на основе выбранного сценария
            // Формула для стохастической модели с трендом и волатильностью
            const randomFactor = 1 + (Math.random() * 2 - 1) * volatilityFactor;
            currentExchangeRate = currentExchangeRate * (1 + monthlyRateChange) * randomFactor;
            
            // Добавляем возможные кризисные скачки для сценария "crisis"
            if (exchangeRateScenario === 'crisis' && Math.random() < 0.05) { // 5% шанс кризисного скачка
                currentExchangeRate *= (1 + (Math.random() * 0.2)); // Скачок до +20%
            }

            // Рассчитываем ежемесячную комиссию (если есть)
            let monthlyFeePayment = 0;
            if (includeFees && monthlyFee > 0) {
                if (monthlyFeeType === 'percent') {
                    monthlyFeePayment = currentLoanBalance * (monthlyFee / 100) / 12;
                } else { // fixed
                    monthlyFeePayment = monthlyFee;
                }
                totalMonthlyFeesPaid += monthlyFeePayment;
            }

            // Рассчитываем досрочное погашение
            let currentEarlyRepayment = 0;
            if (includeEarlyRepayment && earlyRepaymentAmount > 0 && currentMonth >= earlyRepaymentStartMonth && currentLoanBalance > 0) {
                const isRepaymentMonth =
                    (earlyRepaymentFrequencyMonths === -1 && currentMonth === earlyRepaymentStartMonth) ||
                    (earlyRepaymentFrequencyMonths > 0 && (currentMonth - earlyRepaymentStartMonth) % earlyRepaymentFrequencyMonths === 0);

                if (isRepaymentMonth) {
                    const maxEarlyRepayment = Math.max(0, currentLoanBalance - principalPaymentScheduled);
                    currentEarlyRepayment = Math.min(earlyRepaymentAmount, maxEarlyRepayment);
                }
            }

            // Рассчитываем общую сумму платежей за месяц
            const totalPaymentThisMonth = monthlyPaymentBase + monthlyFeePayment + currentEarlyRepayment;

            // Рассчитываем эквивалент в рублях
            const monthlyPaymentRUB = monthlyPaymentBase * currentExchangeRate;
            const totalPaymentRUB = totalPaymentThisMonth * currentExchangeRate;

            // Обновляем итоговые суммы
            totalPrincipalPaidScheduled += principalPaymentScheduled;
            totalInterestPaid += interestPayment;
            totalEarlyRepaymentPaid += currentEarlyRepayment;

            // Обновляем остаток кредита
            const startingBalance = currentLoanBalance;
            currentLoanBalance -= principalPaymentScheduled;
            currentLoanBalance -= currentEarlyRepayment;
            currentLoanBalance = Math.max(0, currentLoanBalance);

            // Пересчитываем аннуитетный платеж после досрочного погашения
            if (currentEarlyRepayment > 0 && paymentType === 'annuity' && currentLoanBalance > 0.005) {
                remainingTermMonthsForCalc = initialLoanTermMonths - currentMonth;
                if (remainingTermMonthsForCalc > 0) {
                    if (earlyRepaymentType === 'payment') {
                        // Для уменьшения платежа - пересчитываем аннуитет с новым остатком и тем же сроком
                        fixedAnnuityPayment = calculateAnnuityPayment(currentLoanBalance, remainingTermMonthsForCalc, initialAnnualRate);
                    } else {
                        // Для уменьшения срока - оставляем тот же платеж
                    }
                } else {
                    fixedAnnuityPayment = 0;
                }
            }

            // Добавляем запись в график платежей
            calculatedSchedule.push({
                month: currentMonth,
                date: paymentDate,
                payment: monthlyPaymentBase,
                principalPayment: principalPaymentScheduled,
                interestPayment: interestPayment,
                startingBalance: startingBalance,
                remainingBalance: currentLoanBalance,
                earlyRepayment: currentEarlyRepayment,
                monthlyFee: monthlyFeePayment,
                totalPayment: totalPaymentThisMonth,
                exchangeRate: currentExchangeRate,
                paymentRUB: monthlyPaymentRUB,
                totalPaymentRUB: totalPaymentRUB
            });
        }

        if (currentMonth >= maxIterations && currentLoanBalance > 0.005) {
            console.warn("Расчет прерван из-за превышения максимального количества итераций.");
            setErrors({ general: "Расчет прерван из-за превышения максимального количества итераций. Проверьте параметры, особенно срок и досрочное погашение." });
            setResults(null);
            setSchedule([]);
            setRiskAnalysis(null);
            setIsCalculating(false);
            return;
        }

        const effectiveTermMonths = calculatedSchedule.length;

        // --- Готовим итоговые результаты ---
        const totalPrincipalPaid = totalPrincipalPaidScheduled + totalEarlyRepaymentPaid;
        const totalCreditCost = totalInterestPaid + totalInitialFeePaid + totalMonthlyFeesPaid;
        const totalPaymentOverall = initialLoanAmount + totalCreditCost;
        const actualMonthlyPayment = effectiveTermMonths > 0 ? calculatedSchedule.slice(0, Math.min(12, effectiveTermMonths)).reduce((sum, item) => sum + item.payment, 0) / Math.min(12, effectiveTermMonths) : 0;
        
        // Расчет средневзвешенного курса за весь период
        const weightedExchangeRate = calculatedSchedule.reduce((sum, item) => sum + item.exchangeRate, 0) / calculatedSchedule.length;
        
        // Расчет общей суммы в рублях
        const totalPaymentRUB = calculatedSchedule.reduce((sum, item) => sum + item.totalPaymentRUB, 0);
        const initialLoanAmountRUB = initialLoanAmount * currencyRate;
        const totalCreditCostRUB = totalPaymentRUB - initialLoanAmountRUB;

        const baseMonthlyPaymentRUB = (calculatedSchedule.length > 0) ? calculatedSchedule[0].paymentRUB : (baseMonthlyPayment * currencyRate);
        
        const finalResults = {
            loanAmount: initialLoanAmount,
            loanAmountRUB: initialLoanAmountRUB,
            interestRate: initialAnnualRate,
            loanTermYears: initialLoanTermYears,
            loanTermMonths: initialLoanTermMonths,
            paymentType: paymentType,
            currency: selectedCurrency,

            effectiveTermMonths: effectiveTermMonths,
            firstMonthlyPayment: calculatedSchedule.length > 0 ? calculatedSchedule[0].payment : 0,
            firstMonthlyPaymentRUB: calculatedSchedule.length > 0 ? calculatedSchedule[0].paymentRUB : 0,
            averageMonthlyPayment: actualMonthlyPayment,
            lastMonthlyPayment: calculatedSchedule.length > 0 ? calculatedSchedule[calculatedSchedule.length - 1].payment : 0,
            lastMonthlyPaymentRUB: calculatedSchedule.length > 0 ? calculatedSchedule[calculatedSchedule.length - 1].paymentRUB : 0,
            
            totalPrincipalPaid: totalPrincipalPaid,
            totalInterest: totalInterestPaid,
            totalFees: totalInitialFeePaid + totalMonthlyFeesPaid,
            totalInitialFee: totalInitialFeePaid,
            totalMonthlyFees: totalMonthlyFeesPaid,
            
            totalEarlyRepayment: totalEarlyRepaymentPaid,
            totalCreditCost: totalCreditCost,
            totalPayment: totalPaymentOverall,
            overpaymentRatio: initialLoanAmount > 0 ? totalCreditCost / initialLoanAmount : 0,
            
            // Информация в рублях
            totalPaymentRUB: totalPaymentRUB,
            totalCreditCostRUB: totalCreditCostRUB,
            initialExchangeRate: currencyRate,
            finalExchangeRate: calculatedSchedule.length > 0 ? calculatedSchedule[calculatedSchedule.length - 1].exchangeRate : currencyRate,
            averageExchangeRate: weightedExchangeRate,
            currencyVolatility: volatilityFactor,
            
            loanStartDate: startDateObj,
            loanEndDate: calculatedSchedule.length > 0 ? calculatedSchedule[calculatedSchedule.length - 1].date : null,
            
            scheduleShort: calculatedSchedule.slice(0, 12) // Первые 12 месяцев для предпросмотра
        };

        // Сохраняем результаты анализа рисков
        const riskAnalysisResults = {
            currencyRiskLevel: riskLevel,
            recommendations: riskRecommendations,
            baseMonthlyPayment: baseMonthlyPayment,
            baseMonthlyPaymentRUB: baseMonthlyPaymentRUB,
            worstCasePaymentRUB: worstCaseMonthlyPaymentRub,
            bestCasePaymentRUB: bestCaseMonthlyPaymentRub,
            currentDTI: currentDTI,
            worstCaseDTI: worstCaseDTI,
            riskIndex: riskIndex,
            hasIncomeInForeignCurrency: hasIncomeInForeignCurrency,
            monthlyIncomeRUB: monthlyIncomeRUB,
            monthlyIncomeForeign: monthlyIncomeForeign,
            initialExchangeRate: currencyRate,
            scenarioName: exchangeRateScenario,
            toleranceName: currencyRiskTolerance,
            
            // Прогнозные данные
            exchangeRateForecast: {
                scenario: exchangeRateScenario,
                startRate: currencyRate,
                endRate: calculatedSchedule.length > 0 ? calculatedSchedule[calculatedSchedule.length - 1].exchangeRate : currencyRate,
                maxRate: Math.max(...calculatedSchedule.map(item => item.exchangeRate)),
                minRate: Math.min(...calculatedSchedule.map(item => item.exchangeRate)),
                volatility: volatilityFactor * 100, // в процентах
                trend: monthlyRateChange * 100 * 12, // годовое изменение в процентах
            }
        };

        setResults(finalResults);
        setSchedule(calculatedSchedule);
        setRiskAnalysis(riskAnalysisResults);
      } catch (error) {
        console.error("Ошибка расчета:", error);
        setErrors({ general: `Произошла ошибка при расчете: ${error.message}. Пожалуйста, проверьте введенные данные или попробуйте позже.` });
        setResults(null);
        setSchedule([]);
        setRiskAnalysis(null);
      } finally {
        setIsCalculating(false);
      }
    }, 50);
  }, [formData, validateForm, errors, setErrors, setResults, setSchedule, setRiskAnalysis, supportedCurrencies, currencyRates, setIsCalculating]);

  // Сброс формы
  const resetForm = () => {
    setFormData({
      loanAmount: '10000000',
      currency: 'USD',
      equivalentInRubles: '900000',
      loanTerm: '15',
      interestRate: '4.5',
      paymentType: 'annuity',
      startDate: new Date().toISOString().split('T')[0],
      loanPurpose: 'mortgage',
      includeEarlyRepayment: false,
      earlyRepaymentType: 'reduction',
      earlyRepaymentAmount: '1000',
      earlyRepaymentPeriod: 'monthly',
      earlyRepaymentStartMonth: '6',
      includeFees: false,
      initialFee: '500',
      monthlyFee: '0',
      monthlyFeeType: 'fixed',
      initialDownPayment: '3000000',
      hasIncomeInForeignCurrency: false,
      monthlyIncomeRUB: '150000',
      monthlyIncomeForeign: '0',
      currencyRiskTolerance: 'medium',
      exchangeRateScenario: 'moderate',
    });
    setResults(null);
    setSchedule([]);
    setRiskAnalysis(null);
    setErrors({});
    setSaveLoanName('');
    setShowSaveDialog(false);
  };

  // Экспорт графика платежей в CSV
  const exportToCSV = () => {
    if (!schedule || schedule.length === 0 || !results) {
      alert("Нет данных для экспорта. Сначала выполните расчет.");
      return;
    }

    // Определяем заголовки на основе расчетов
    const headers = ['Месяц', 'Дата', `Платеж (${formData.currency})`, 'Основной долг', 'Проценты', 'Курс', `Платеж (₽)`];
    if (results.totalMonthlyFees > 0) headers.push('Комиссия');
    if (results.totalEarlyRepayment > 0) headers.push('Досрочное погашение');
    headers.push(`Общий платеж (${formData.currency})`, `Общий платеж (₽)`, `Остаток долга (${formData.currency})`);

    const formatCsvNumber = (num) => num.toFixed(2).replace('.', ',');

    const rows = schedule.map(item => {
      const rowData = [
        item.month,
        item.date.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' }),
        formatCsvNumber(item.payment),
        formatCsvNumber(item.principalPayment),
        formatCsvNumber(item.interestPayment),
        formatCsvNumber(item.exchangeRate),
        formatCsvNumber(item.paymentRUB),
      ];
      if (results.totalMonthlyFees > 0) rowData.push(formatCsvNumber(item.monthlyFee));
      if (results.totalEarlyRepayment > 0) rowData.push(formatCsvNumber(item.earlyRepayment));
      rowData.push(
        formatCsvNumber(item.totalPayment),
        formatCsvNumber(item.totalPaymentRUB),
        formatCsvNumber(item.remainingBalance)
      );
      return rowData.join(';');
    });

    // Добавим итоговую строку
    const totalRow = [
      '', 'ИТОГО:', 
      formatCsvNumber(schedule.reduce((sum, item) => sum + item.payment, 0)),
      formatCsvNumber(results.totalPrincipalPaid),
      formatCsvNumber(results.totalInterest),
      formatCsvNumber(results.averageExchangeRate),
      formatCsvNumber(schedule.reduce((sum, item) => sum + item.paymentRUB, 0))
    ];
    if (results.totalMonthlyFees > 0) totalRow.push(formatCsvNumber(results.totalMonthlyFees));
    if (results.totalEarlyRepayment > 0) totalRow.push(formatCsvNumber(results.totalEarlyRepayment));
    totalRow.push(
      formatCsvNumber(results.totalPayment),
      formatCsvNumber(results.totalPaymentRUB),
      ''
    );
    
    // Добавим основную информацию в начало файла
    const infoRows = [
      [`Валютная ипотека (${formData.currency})`, ''],
      ['Сумма кредита:', `${formatCsvNumber(results.loanAmount)} ${formData.currency} (${formatCsvNumber(results.loanAmountRUB)} ₽)`],
      ['Срок кредита:', `${results.loanTermMonths} мес.`],
      ['Процентная ставка:', `${results.interestRate}%`],
      ['Тип платежа:', formData.paymentType === 'annuity' ? 'Аннуитетный' : 'Дифференцированный'],
      ['Дата начала кредита:', new Date(formData.startDate).toLocaleDateString('ru-RU')],
      ['Переплата:', `${formatCsvNumber(results.totalCreditCost)} ${formData.currency} (${formatCsvNumber(results.totalCreditCostRUB)} ₽)`],
      ['Полная стоимость кредита:', `${formatCsvNumber(results.totalPayment)} ${formData.currency} (${formatCsvNumber(results.totalPaymentRUB)} ₽)`],
      ['Первоначальный курс:', formatCsvNumber(results.initialExchangeRate)],
      ['Средний курс за период:', formatCsvNumber(results.averageExchangeRate)],
      ['Конечный курс:', formatCsvNumber(results.finalExchangeRate)],
      [''],  // Пустая строка для разделения
    ].map(row => row.join(';'));

    // UTF-8 BOM для правильной кодировки в Excel
    const csvContent = '\uFEFF' + [...infoRows, headers.join(';'), ...rows, totalRow.join(';')].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `валютная_ипотека_${formData.currency}_график_платежей.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Обработчик сохранения кредита
  const handleSaveLoan = () => {
    if (showSaveDialog) {
      const savedResult = saveLoan(saveLoanName || null);
      if (savedResult) {
        alert(`Валютная ипотека "${savedResult.name}" успешно сохранена!`);
      }
      setShowSaveDialog(false);
      setSaveLoanName('');
    } else {
      setShowSaveDialog(true);
    }
  };

  // Обработчик отправки формы
  const handleSubmit = (e) => {
    e.preventDefault();
    calculatePaymentSchedule();
  };

  return (
    <form onSubmit={handleSubmit} noValidate>
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md border border-gray-200 dark:border-gray-700 p-6 sm:p-8 mb-8">
        <h2 className="text-2xl font-semibold mb-6 text-gray-900 dark:text-white flex items-center gap-2">
          <BarChart2 className="w-6 h-6 text-indigo-500" />
          Параметры валютной ипотеки
        </h2>

        {/* Общая ошибка */}
        {errors.general && (
          <div role="alert" className="mb-4 p-3 bg-red-100 dark:bg-red-900/30 border border-red-300 dark:border-red-600 rounded-md text-red-700 dark:text-red-300 text-sm">
            {errors.general}
          </div>
        )}

        {/* Информационное сообщение о валютных рисках */}
        <div className="mb-6 p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="text-sm font-medium text-amber-800 dark:text-amber-300 mb-1">
              Важно: валютная ипотека сопряжена с дополнительными рисками
            </h3>
            <p className="text-sm text-amber-700 dark:text-amber-400">
              В случае существенного изменения курса валют, сумма платежа в рублях может значительно вырасти. 
              Анализируйте свои финансовые возможности и валютные риски перед принятием решения.
            </p>
          </div>
        </div>

        {/* Основные параметры ипотеки */}
        <MortgageParametersSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors} 
          supportedCurrencies={supportedCurrencies}
          currencyRates={currencyRates}
        />

        {/* Валютные параметры */}
        <CurrencyOptionsSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors}
          supportedCurrencies={supportedCurrencies}
          currencyRates={currencyRates}
        />

        {/* Дополнительные параметры */}
        <AdditionalParametersSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors} 
        />

        {/* Досрочное погашение */}
        <EarlyRepaymentSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors} 
          earlyRepaymentPeriods={earlyRepaymentPeriods}
        />

        {/* Комиссии */}
        <FeesSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors} 
        />
        
        {/* Анализ рисков */}
        <RiskSection
          formData={formData}
          handleInputChange={handleInputChange}
          errors={errors}
          exchangeRateScenarios={exchangeRateScenarios}
          riskToleranceLevels={riskToleranceLevels}
        />

        {/* Диалог сохранения кредита */}
        {showSaveDialog && (
          <div className="mt-4 mb-6 p-4 bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-100 dark:border-indigo-800 rounded-lg">
            <div className="flex items-center">
              <input
                type="text"
                placeholder="Введите название для сохранения"
                value={saveLoanName}
                onChange={(e) => setSaveLoanName(e.target.value)}
                className="form-input flex-grow mr-3"
                autoFocus
              />
              <Button
                type="button"
                onClick={handleSaveLoan}
                primary
                size="sm"
              >
                Сохранить
              </Button>
            </div>
          </div>
        )}

        {/* Кнопки действий */}
        <div className="mt-8 flex flex-wrap gap-4">
          <Button
            type="submit"
            primary
            icon={<BarChart2 className="w-5 h-5" />}
            disabled={isCalculating || Object.keys(errors).length > 0}
            isLoading={isCalculating}
            size="lg"
          >
            {isCalculating ? 'Рассчитываем...' : 'Рассчитать ипотеку'}
          </Button>
          <Button
            type="button"
            onClick={resetForm}
            icon={<RefreshCw className="w-5 h-5" />}
            variant="secondary"
            disabled={isCalculating}
            size="lg"
          >
            Сбросить
          </Button>
          {schedule.length > 0 && (
            <Button
              type="button"
              onClick={exportToCSV}
              icon={<Download className="w-5 h-5" />}
              variant="secondary"
              disabled={isCalculating}
              size="lg"
            >
              Скачать график (CSV)
            </Button>
          )}
          {results && !showSaveDialog && (
            <Button
              type="button"
              onClick={handleSaveLoan}
              icon={<Save className="w-5 h-5" />}
              variant="secondary"
              disabled={isCalculating}
              size="lg"
            >
              Сохранить расчет
            </Button>
          )}
        </div>
        {Object.keys(errors).length > 0 && !errors.general && (
          <p className="mt-4 text-sm text-red-600 dark:text-red-400" role="alert">Пожалуйста, исправьте ошибки в форме.</p>
        )}
      </div>
    </form>
  );
};

export default CalculationForm;